import { Link } from "react-router-dom";
import { usePageMeta } from "@/lib/meta";
import Reveal, { Overline } from "@/components/Reveal";

const SECTIONS = [
  {
    title: "What this site collects",
    body: [
      "When you submit the consultation request form or complete the Executive Presence Self-Assessment, Credible Presence collects the information you choose to provide: your name, email address, phone number (optional), role and organization, service interest, preferred meeting format, and your description of your communication goals or challenges. The self-assessment also stores your ratings for each statement.",
      "This information is provided voluntarily. No account is created, and no payment information is collected on this site.",
    ],
  },
  {
    title: "How consultation inquiries are handled",
    body: [
      "Every inquiry is read personally and treated as confidential. Your information is used for one purpose only: to respond to you and to prepare for a possible consultation. It is never sold, rented, or shared with third parties for marketing, and it is never added to an email list without your explicit agreement.",
      "Consultation requests and self-assessment responses are stored securely and retained only as long as needed to handle your inquiry and any engagement that follows.",
    ],
  },
  {
    title: "About the self-assessment",
    body: [
      "The Executive Presence Self-Assessment is a self-reflection tool. Your results are a snapshot of your own perceptions — they are not a professional evaluation of any kind.",
      "Nothing on this website constitutes medical advice, diagnosis, or treatment. Credible Presence provides communication coaching, not medical or clinical speech services. If you have concerns about your vocal health, please consult a physician or a licensed speech-language pathologist.",
    ],
  },
  {
    title: "Your choices",
    body: [
      "You may ask at any time to review, correct, or delete the information you have submitted. Simply reply to any correspondence or reach out through the consultation form, and your request will be handled promptly.",
      "This site does not use advertising trackers. Standard, privacy-respecting technical logs (such as pages visited) may be kept by the hosting provider to keep the site secure and reliable.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "If this policy changes, the updated version will be posted on this page. Material changes will be noted clearly.",
    ],
  },
];

export default function Privacy() {
  usePageMeta(
    "Privacy Policy | Credible Presence",
    "How Credible Presence collects, uses, and protects the information you share through consultation inquiries and the Executive Presence Self-Assessment. Confidential, never sold, never shared."
  );

  return (
    <div data-testid="privacy-page" className="bg-[#F7F5F1] pt-40 pb-24 sm:pt-48">
      <div className="mx-auto max-w-3xl px-6 sm:px-8">
        <Reveal>
          <Overline>Privacy Policy</Overline>
          <h1 className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-[#26282E] sm:text-5xl">
            Your words stay between us.
          </h1>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-[#8A8D94]">
            Last updated: July 2026
          </p>
          <p className="mt-8 text-base leading-relaxed text-[#5B5E66] sm:text-lg">
            Confidentiality is not a feature of this practice — it is the premise. This policy explains, plainly,
            what information this website collects and how it is handled.
          </p>
        </Reveal>
        <div className="mt-14 space-y-12">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={Math.min(i * 0.06, 0.2)}>
              <section data-testid={`privacy-section-${i + 1}`}>
                <h2 className="font-serif text-2xl tracking-tight text-[#26282E]">{s.title}</h2>
                <div className="mt-4 space-y-4">
                  {s.body.map((p, j) => (
                    <p key={j} className="text-[15px] leading-relaxed text-[#5B5E66]">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-14 border-t border-[#26282E]/10 pt-8 text-sm text-[#8A8D94]">
            Questions about this policy? Use the{" "}
            <Link to="/contact" data-testid="privacy-contact-link" className="text-[#2F5D8C] underline-offset-4 hover:underline">
              consultation form
            </Link>{" "}
            and your message will be treated with the same confidentiality as any inquiry.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
